import type { MediaProbe } from "@/lib/ffmpegEngine";

/**
 * How to merge clips that are NOT the same size / frame rate / audio layout. The old command joined the
 * clips as they were, so any mismatch (portrait + landscape, 720p + 360p, a clip without sound) failed.
 * Every clip is first brought to one size (letterboxed, never stretched), one frame rate and one audio
 * format; a clip without sound gets silence of its own length. When all clips already match, they are
 * joined without re-encoding (seconds, no quality loss).
 */

const even = (n: number) => Math.max(2, Math.round(n / 2) * 2);

export interface MergePlan {
  mode: "copy" | "reencode";
  width: number;
  height: number;
  fps: number;
  withAudio: boolean;
  filter: string;
  /** true when at least one clip had to be resized (letterboxed or cropped) to the output size */
  fitted: boolean;
  /** true when at least one clip ran at a different frame rate than the output (the frames were converted) */
  fpsMismatch: boolean;
  /** Seconds each join overlaps as a crossfade: 0 = hard cut. Can be less than asked for when a clip is very short. */
  crossfade: number;
  /** Length of the merged video in seconds, from the probed clip lengths (shorter than the sum by one overlap per join when crossfading). */
  totalSeconds: number;
}

/** Overlap used when "Fade between clips" is on. */
export const CROSSFADE_SECONDS = 0.5;

export type MergeShape = "first" | "landscape" | "portrait" | "square";
const SHAPES: Record<Exclude<MergeShape, "first">, [number, number]> = { landscape: [1920, 1080], portrait: [1080, 1920], square: [1080, 1080] };

const COPY_VIDEO = new Set(["h264"]);
const COPY_AUDIO = new Set(["aac"]);

/** True when every clip has the same codecs and parameters, so they can be joined without re-encoding. */
export function canStreamCopy(probes: MediaProbe[]): boolean {
  const a = probes[0];
  if (!a || !a.hasVideo || !COPY_VIDEO.has(a.vcodec)) return false;
  if (a.hasAudio && !COPY_AUDIO.has(a.acodec)) return false;
  return probes.every(
    (p) =>
      p.hasVideo && p.vcodec === a.vcodec && p.width === a.width && p.height === a.height && Math.abs(p.fps - a.fps) < 0.01 &&
      p.pixFmt === a.pixFmt && p.hasAudio === a.hasAudio && p.acodec === a.acodec && p.sampleRate === a.sampleRate && p.channels === a.channels,
  );
}

/** The overlap that can really be used: the asked one, shrunk so it never exceeds 40% of the shortest clip; 0 below 0.1 s. */
export function usableCrossfade(probes: MediaProbe[], asked: number): number {
  if (!(asked > 0) || probes.length < 2) return 0;
  const shortest = Math.min(...probes.map((p) => (p.duration > 0 ? p.duration : 1)));
  const d = Math.floor(Math.min(asked, shortest * 0.4) * 100) / 100;
  return d >= 0.1 ? d : 0;
}

export function planMerge(probes: MediaProbe[], shape: MergeShape = "first", fill = false, crossfadeSeconds = 0): MergePlan {
  const first = probes[0];
  const [width, height] = shape === "first" ? [even(first.width || 1280), even(first.height || 720)] : SHAPES[shape];
  const fps = Math.min(60, Math.max(10, Math.round(first.fps || 30)));
  const withAudio = probes.some((p) => p.hasAudio);
  const xf = usableCrossfade(probes, crossfadeSeconds);
  const dur = (p: MediaProbe) => Math.max(0.1, p.duration || 1);
  const parts: string[] = [];
  const refs: string[] = [];
  probes.forEach((p, i) => {
    parts.push(
      `[${i}:v]${fill ? `scale=${width}:${height}:force_original_aspect_ratio=increase,crop=${width}:${height}` : `scale=${width}:${height}:force_original_aspect_ratio=decrease,pad=${width}:${height}:(ow-iw)/2:(oh-ih)/2:color=black`},setsar=1,fps=${fps},format=yuv420p,setpts=PTS-STARTPTS[v${i}]`,
    );
    if (withAudio) {
      // With a crossfade the overlap is computed from the clip lengths, so each audio track is padded or cut to its clip's length first.
      const exact = xf ? `,apad,atrim=duration=${dur(p).toFixed(3)}` : "";
      if (p.hasAudio) parts.push(`[${i}:a]aresample=44100,aformat=sample_fmts=fltp:channel_layouts=stereo${exact},asetpts=PTS-STARTPTS[a${i}]`);
      else parts.push(`anullsrc=r=44100:cl=stereo,atrim=duration=${dur(p).toFixed(3)},asetpts=PTS-STARTPTS[a${i}]`);
      refs.push(`[v${i}][a${i}]`);
    } else refs.push(`[v${i}]`);
  });
  let filter: string;
  let totalSeconds = probes.reduce((n, p) => n + (p.duration > 0 ? p.duration : 0), 0);
  if (xf) {
    // Chain of xfade (picture) and acrossfade (sound): each join starts one overlap before the end of what is built so far.
    const last = probes.length - 1;
    let built = dur(probes[0]);
    let vPrev = "[v0]", aPrev = "[a0]";
    for (let i = 1; i <= last; i++) {
      const vOut = i === last ? "[outv]" : `[xv${i}]`, aOut = i === last ? "[outa]" : `[xa${i}]`;
      parts.push(`${vPrev}[v${i}]xfade=transition=fade:duration=${xf}:offset=${(built - xf).toFixed(3)}${vOut}`);
      if (withAudio) parts.push(`${aPrev}[a${i}]acrossfade=d=${xf}:c1=tri:c2=tri${aOut}`);
      built += dur(probes[i]) - xf;
      vPrev = vOut; aPrev = aOut;
    }
    filter = parts.join(";");
    totalSeconds = built;
  } else {
    filter = `${parts.join(";")};${refs.join("")}concat=n=${probes.length}:v=1:a=${withAudio ? 1 : 0}[outv]${withAudio ? "[outa]" : ""}`;
  }
  const fitted = probes.some((p) => p.width !== width || p.height !== height);
  const fpsMismatch = probes.some((p) => p.fps > 0 && Math.abs(p.fps - fps) > 0.5);
  return { mode: shape === "first" && !xf && canStreamCopy(probes) ? "copy" : "reencode", width, height, fps, withAudio, filter, fitted, fpsMismatch, crossfade: xf, totalSeconds };
}

export function mergeEncodeArgs(plan: MergePlan): string[] {
  return [
    "-filter_complex", plan.filter,
    "-map", "[outv]",
    ...(plan.withAudio ? ["-map", "[outa]"] : []),
    "-c:v", "libx264", "-preset", "veryfast", "-crf", "24", "-pix_fmt", "yuv420p",
    ...(plan.withAudio ? ["-c:a", "aac", "-b:a", "128k"] : []),
    "-movflags", "+faststart",
    "output.mp4",
  ];
}
